import React from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useSettings } from "../../contexts/SettingsContext";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X, FileText, Send, Download, ShoppingCart, Search, MessageCircle, UserSearch, CheckCircle2, ArrowRightCircle } from "lucide-react";
import CityCombobox from "../../components/CityCombobox";

const STATUS_LABELS = {
    draft: "Ciornă", issued: "Emisă", paid: "Plătită", cancelled: "Anulată",
    sent: "Trimisă", accepted: "Acceptată", rejected: "Respinsă", expired: "Expirată",
};
const STATUS_OPTIONS = {
    invoice: ["draft", "issued", "paid", "cancelled"],
    proforma: ["draft", "issued", "paid", "cancelled"],
    offer: ["draft", "sent", "accepted", "rejected", "expired"],
};
const KIND_LABELS = { invoice: "Factură", proforma: "Proformă", offer: "Ofertă" };

const emptyClient = { name: "", phone: "", email: "", address: "", city: "", county: "", postal_code: "", company: "", cui: "" };
const emptyItem = (vat = 19) => ({ name: "", qty: 1, unit_price: 0, vat_rate: vat, unit: "buc" });

export default function AdminInvoices() {
    const { settings } = useSettings();
    // Always use the configured rate as placeholder/default — even if not a VAT payer
    const defaultVat = React.useMemo(() => {
        const v = Number(settings?.default_vat_rate);
        return Number.isFinite(v) ? v : 19;
    }, [settings]);

    const [items, setItems] = React.useState([]);
    const [tab, setTab] = React.useState("all");  // all | invoice | proforma | offer
    const [editing, setEditing] = React.useState(null);
    const [search, setSearch] = React.useState("");
    const [clientSearch, setClientSearch] = React.useState("");
    const [clientResults, setClientResults] = React.useState([]);
    const [showClientPicker, setShowClientPicker] = React.useState(false);
    const [productSuggs, setProductSuggs] = React.useState({ idx: -1, results: [] });
    const [counties, setCounties] = React.useState([]);
    const [cities, setCities] = React.useState([]);

    React.useEffect(() => {
        api.get("/locations/counties").then(r => setCounties(r.data || [])).catch(() => {});
    }, []);
    React.useEffect(() => {
        const county = editing?.client?.county;
        if (county) api.get("/locations/cities", { params: { county } }).then(r => setCities(r.data || [])).catch(() => {});
        else setCities([]);
    }, [editing?.client?.county]);

    const load = React.useCallback(() => {
        const params = new URLSearchParams();
        if (tab !== "all") params.set("kind", tab);
        if (search) params.set("q", search);
        api.get("/admin/invoices?" + params.toString()).then(r => setItems(r.data || [])).catch(() => {});
    }, [tab, search]);

    React.useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

    const [sp] = useSearchParams();
    React.useEffect(() => {
        const id = sp.get("id");
        if (id) {
            api.get(`/admin/invoices/${id}`).then(r => setEditing({ ...r.data })).catch(() => toast.error("Factură inexistentă"));
        }
    }, [sp]);

    const openNew = (kind = "invoice") => {
        const seriesByKind = { proforma: "PRO", offer: "OFR", invoice: "MAN" };
        setEditing({
            kind, series: seriesByKind[kind] || "MAN",
            issue_date: new Date().toISOString().slice(0, 10),
            due_date: "",
            client: { ...emptyClient },
            items: [emptyItem(defaultVat)],
            vat_rate: defaultVat,
            currency: "RON",
            payment_method: "",
            notes: "",
        });
    };

    const openEdit = async (id) => {
        const r = await api.get(`/admin/invoices/${id}`);
        setEditing({ ...r.data });
    };

    const save = async () => {
        if (!editing.client?.name) return toast.error("Numele clientului este obligatoriu");
        if (!(editing.items || []).length) return toast.error("Adaugă cel puțin un articol");
        try {
            if (editing.invoice_id) {
                await api.put(`/admin/invoices/${editing.invoice_id}`, editing);
            } else {
                await api.post("/admin/invoices", editing);
            }
            toast.success("Salvat"); setEditing(null); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
    };

    const del = async (id) => {
        if (!window.confirm("Sigur ștergi?")) return;
        await api.delete(`/admin/invoices/${id}`); load(); toast.success("Șters");
    };

    const downloadPdf = async (id, no) => {
        try {
            const r = await api.get(`/admin/invoices/${id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
            const a = document.createElement("a"); a.href = url; a.download = `${no || id}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 2000);
        } catch { toast.error("Eroare la descărcare"); }
    };

    const sendEmail = async (id) => {
        if (!window.confirm("Trimit documentul pe email către client?")) return;
        try {
            const r = await api.post(`/admin/invoices/${id}/email`);
            toast.success(`Trimis la ${r.data.sent_to}`); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare la trimitere"); }
    };

    const sendWhatsApp = async (id) => {
        try {
            const r = await api.get(`/admin/invoices/${id}/whatsapp`);
            window.open(r.data.url, "_blank", "noopener,noreferrer");
        } catch (e) { toast.error("Eroare la generarea linkului WhatsApp"); }
    };

    const navigate = useNavigate();
    const convertToOrder = async (it) => {
        if (it.converted_order_id) {
            // Already converted — jump to that order
            navigate(`/admin/orders?id=${it.converted_order_id}`);
            return;
        }
        const label = it.kind === "offer" ? "Convertesc oferta în comandă (status Acceptată)?" :
                      it.kind === "proforma" ? "Convertesc proforma în comandă fermă?" :
                      "Convertesc acest document într-o comandă?";
        if (!window.confirm(label)) return;
        try {
            const r = await api.post(`/admin/invoices/${it.invoice_id}/convert-to-order`, {});
            toast.success(`Comandă creată: ${r.data.order_number}`);
            load();
            navigate(`/admin/orders?id=${r.data.order_id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eroare la conversie");
        }
    };

    const searchClients = React.useCallback(async (q) => {
        if (!q || q.length < 2) { setClientResults([]); return; }
        try {
            const r = await api.get(`/admin/clients/search?q=${encodeURIComponent(q)}`);
            const d = r.data || {};
            // Flatten all source arrays into one list, adding a _source tag for display
            const all = [
                ...(d.registered || []).map(c => ({ ...c, _source: "cont" })),
                ...(d.offline || []).map(c => ({ ...c, _source: "offline" })),
                ...(d.crm || []).map(c => ({ ...c, _source: "crm" })),
                ...(d.orders || []).map(c => ({ ...c, _source: "comandă" })),
            ];
            // Deduplicate by email+phone
            const seen = new Set();
            const unique = all.filter(c => {
                const key = (c.email || "") + "|" + (c.phone || "") + "|" + (c.name || "");
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
            setClientResults(unique.slice(0, 12));
        } catch { setClientResults([]); }
    }, []);

    React.useEffect(() => {
        const t = setTimeout(() => searchClients(clientSearch), 300);
        return () => clearTimeout(t);
    }, [clientSearch, searchClients]);

    // Normalize a county value (possibly ASCII/mismatched) to the exact name in the counties list
    const normalizeCounty = React.useCallback((raw) => {
        if (!raw || !counties.length) return raw || "";
        if (counties.includes(raw)) return raw;
        const strip = (s) => s.toLowerCase()
            .replace(/[ăâ]/g, "a").replace(/î/g, "i")
            .replace(/[șş]/g, "s").replace(/[țţ]/g, "t");
        const stripped = strip(raw);
        return counties.find(c => strip(c) === stripped) || raw;
    }, [counties]);

    const pickClient = (c) => {
        setEditing(v => ({
            ...v,
            client: {
                name: c.name || "",
                phone: c.phone || "",
                email: c.email || "",
                address: c.address || "",
                city: c.city || "",
                county: normalizeCounty(c.county || ""),
                postal_code: c.postal_code || "",
                company: c.company || "",
                cui: c.cui || "",
            },
            client_id: c.client_id || null,
            user_id: c.user_id || null,
        }));
        setClientSearch("");
        setClientResults([]);
    };

    // unit_price is inclusive of VAT — extract VAT from total
    const totalsLine = () => {
        const items_ = editing.items || [];
        let total = 0; let vat = 0;
        items_.forEach(it => {
            const line = (it.qty || 0) * (it.unit_price || 0);
            total += line;
            const rate = it.vat_rate || 0;
            if (rate > 0) vat += line - line / (1 + rate / 100);
        });
        return { subtotal: total - vat, vat, total };
    };

    const searchProducts = React.useCallback(async (q, idx) => {
        if (!q || q.length < 2) { setProductSuggs({ idx: -1, results: [] }); return; }
        try {
            const r = await api.get("/products", { params: { q, limit: 10 } });
            const list = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.products || []);
            setProductSuggs({ idx, results: list.slice(0, 10) });
        } catch { setProductSuggs({ idx: -1, results: [] }); }
    }, []);

    return (
        <div data-testid="admin-invoices">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                    <div className="overline mb-1">Administrare</div>
                    <h1 className="font-heading text-3xl">Facturi, Proforme & Oferte</h1>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button onClick={() => openNew("offer")} className="btn-outline flex items-center gap-1 text-sm" data-testid="inv-new-offer">
                        <Plus className="h-4 w-4" /> Ofertă
                    </button>
                    <button onClick={() => openNew("proforma")} className="btn-outline flex items-center gap-1 text-sm" data-testid="inv-new-proforma">
                        <Plus className="h-4 w-4" /> Proformă
                    </button>
                    <button onClick={() => openNew("invoice")} className="btn-primary flex items-center gap-1 text-sm" data-testid="inv-new-invoice">
                        <Plus className="h-4 w-4" /> Factură
                    </button>
                </div>
            </div>

            <div className="card-soft p-3 mb-3 flex items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <input value={search} onChange={(e) => setSearch(e.target.value)}
                       placeholder="Caută după număr, client, CUI..."
                       className="input-soft !py-2 flex-1" data-testid="inv-search" />
            </div>

            <div className="flex gap-1 mb-3 border-b border-border">
                {[["all", "Toate"], ["invoice", "Facturi"], ["proforma", "Proforme"], ["offer", "Oferte"]].map(([k, l]) => (
                    <button key={k} onClick={() => setTab(k)}
                            className={`px-4 py-2 text-sm font-medium ${tab === k ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`}
                            data-testid={`inv-tab-${k}`}>
                        {l}
                    </button>
                ))}
            </div>

            <div className="card-soft overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-muted"><tr>
                        {["#", "Tip", "Client", "Data", "Total", "Status", "Acțiuni"].map(h => <th key={h} className="text-left px-3 py-3 text-xs uppercase text-muted-foreground">{h}</th>)}
                    </tr></thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">Nicio factură încă.</td></tr>}
                        {items.map((it) => (
                            <tr key={it.invoice_id} className="border-t border-border" data-testid={`inv-row-${it.invoice_id}`}>
                                <td className="px-3 py-2 font-mono text-xs">{it.invoice_number}</td>
                                <td className="px-3 py-2">
                                    <span className={`chip text-[10px] ${it.kind === "proforma" ? "bg-amber-100 text-amber-900" : it.kind === "offer" ? "bg-blue-100 text-blue-900" : "chip-primary"}`}>
                                        {KIND_LABELS[it.kind] || it.kind}
                                    </span>
                                </td>
                                <td className="px-3 py-2">
                                    <div className="font-medium">{it.client?.name || "—"}</div>
                                    {it.client?.email && <div className="text-xs text-muted-foreground">{it.client.email}</div>}
                                </td>
                                <td className="px-3 py-2 text-xs">{it.issue_date}</td>
                                <td className="px-3 py-2 font-medium">{(it.total || 0).toFixed(2)} {it.currency}</td>
                                <td className="px-3 py-2">
                                    <span className="chip text-[10px] bg-muted">{STATUS_LABELS[it.status] || it.status}</span>
                                    {it.emailed_at && <div className="text-[10px] text-muted-foreground mt-0.5">📧 trimis</div>}
                                </td>
                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                    <button onClick={() => downloadPdf(it.invoice_id, it.invoice_number)} className="btn-ghost" title="Descarcă PDF" data-testid={`inv-download-${it.invoice_id}`}><Download className="h-4 w-4" /></button>
                                    {it.client?.email && <button onClick={() => sendEmail(it.invoice_id)} className="btn-ghost" title="Trimite pe email" data-testid={`inv-email-${it.invoice_id}`}><Send className="h-4 w-4" /></button>}
                                    {it.client?.phone && <button onClick={() => sendWhatsApp(it.invoice_id)} className="btn-ghost text-emerald-600" title="Trimite pe WhatsApp" data-testid={`inv-whatsapp-${it.invoice_id}`}><MessageCircle className="h-4 w-4" /></button>}
                                    {(it.kind === "offer" || it.kind === "proforma") && (
                                        <button onClick={() => convertToOrder(it)} className={`btn-ghost ${it.converted_order_id ? "text-muted-foreground" : "text-blue-600"}`}
                                                title={it.converted_order_id ? `Vezi comanda ${it.converted_order_id.slice(-8)}` : "Convertește în comandă"}
                                                data-testid={`inv-convert-${it.invoice_id}`}>
                                            <ArrowRightCircle className="h-4 w-4" />
                                        </button>
                                    )}
                                    <button onClick={() => openEdit(it.invoice_id)} className="btn-ghost"><Edit2 className="h-4 w-4" /></button>
                                    <button onClick={() => del(it.invoice_id)} className="btn-ghost text-destructive"><Trash2 className="h-4 w-4" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="inv-modal">
                        <div className="flex items-center justify-between">
                            <h2 className="font-heading text-xl">{editing.invoice_id ? "Editează" : "Document nou"}</h2>
                            <button onClick={() => setEditing(null)}><X className="h-5 w-5" /></button>
                        </div>

                        <div className="grid grid-cols-3 gap-2">
                            <select value={editing.kind} onChange={(e) => {
                                const k = e.target.value;
                                const seriesByKind = { proforma: "PRO", offer: "OFR", invoice: "MAN" };
                                setEditing(v => ({ ...v, kind: k, series: seriesByKind[k] || "MAN" }));
                            }} className="input-soft" data-testid="inv-kind">
                                <option value="invoice">Factură</option>
                                <option value="proforma">Proformă</option>
                                <option value="offer">Ofertă</option>
                            </select>
                            <input value={editing.series} onChange={(e) => setEditing(v => ({ ...v, series: e.target.value.toUpperCase() }))}
                                   placeholder="Serie" className="input-soft" />
                            <input type="date" value={editing.issue_date} onChange={(e) => setEditing(v => ({ ...v, issue_date: e.target.value }))} className="input-soft" />
                        </div>

                        <fieldset className="border border-border rounded-lg p-3 space-y-2">
                            <legend className="text-xs uppercase text-muted-foreground px-2 flex items-center gap-2">
                                Client
                                {(editing.client_id || editing.user_id) && (
                                    <span className="chip text-[10px] bg-emerald-100 text-emerald-900 normal-case flex items-center gap-1">
                                        <CheckCircle2 className="h-3 w-3" /> {editing.client_id ? "client offline" : "cont"}
                                    </span>
                                )}
                            </legend>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="relative col-span-2">
                                    <input
                                        placeholder="Nume * — caută client existent pe măsură ce scrii"
                                        value={editing.client?.name || ""}
                                        onChange={(e) => {
                                            const name = e.target.value;
                                            setEditing(v => ({ ...v, client: { ...v.client, name } }));
                                            setClientSearch(name);
                                        }}
                                        onBlur={() => setTimeout(() => setClientResults([]), 200)}
                                        className="input-soft w-full"
                                        data-testid="inv-client-name"
                                    />
                                    {clientResults.length > 0 && (
                                        <div className="absolute z-20 top-full left-0 right-0 bg-card border border-border rounded-xl shadow-lg max-h-48 overflow-y-auto" data-testid="inv-client-results">
                                            {clientResults.map((c, i) => (
                                                <button key={i} type="button" onMouseDown={() => pickClient(c)}
                                                    className="w-full text-left px-3 py-2.5 hover:bg-muted text-sm flex items-center justify-between gap-2 border-b border-border last:border-0">
                                                    <div>
                                                        <div className="font-medium">{c.name || c.email}</div>
                                                        <div className="text-xs text-muted-foreground">{c.phone || ""} {c.email ? "· " + c.email : ""}</div>
                                                    </div>
                                                    <span className="text-[10px] chip bg-muted shrink-0">{c._source || "cont"}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <input placeholder="Email" value={editing.client?.email || ""} onChange={(e) => setEditing(v => ({ ...v, client: { ...v.client, email: e.target.value } }))} className="input-soft" />
                                <input placeholder="Telefon" value={editing.client?.phone || ""} onChange={(e) => setEditing(v => ({ ...v, client: { ...v.client, phone: e.target.value } }))} className="input-soft" />
                                <input placeholder="Companie" value={editing.client?.company || ""} onChange={(e) => setEditing(v => ({ ...v, client: { ...v.client, company: e.target.value } }))} className="input-soft" />
                                <input placeholder="CUI" value={editing.client?.cui || ""} onChange={(e) => setEditing(v => ({ ...v, client: { ...v.client, cui: e.target.value } }))} className="input-soft" />
                                <input placeholder="Adresă" value={editing.client?.address || ""} onChange={(e) => setEditing(v => ({ ...v, client: { ...v.client, address: e.target.value } }))} className="input-soft" />
                                <select value={editing.client?.county || ""} onChange={(e) => setEditing(v => ({ ...v, client: { ...v.client, county: e.target.value, city: "" } }))} className="input-soft">
                                    <option value="">Județ</option>
                                    {counties.map(c => <option key={c}>{c}</option>)}
                                </select>
                                <CityCombobox
                                    cities={cities}
                                    value={editing.client?.city || ""}
                                    onChange={(v) => setEditing(vv => ({ ...vv, client: { ...vv.client, city: v } }))}
                                    onPostalCode={(pc) => setEditing(vv => ({ ...vv, client: { ...vv.client, postal_code: vv.client?.postal_code || pc } }))}
                                    placeholder="Localitate"
                                />
                                <input placeholder="Cod poștal" value={editing.client?.postal_code || ""} onChange={(e) => setEditing(v => ({ ...v, client: { ...v.client, postal_code: e.target.value } }))} className="input-soft" />
                            </div>
                        </fieldset>

                        <fieldset className="border border-border rounded-lg p-3">
                            <legend className="text-xs uppercase text-muted-foreground px-2">Articole</legend>
                            <div className="grid grid-cols-12 gap-1 text-[10px] uppercase tracking-wide text-muted-foreground font-mono px-1 pb-1 border-b border-dashed border-border">
                                <div className="col-span-5">Denumire articol</div>
                                <div className="col-span-1 text-center" title="Unitate de măsură (buc, m, oră, set, serv...)">UM</div>
                                <div className="col-span-1 text-center">Cant.</div>
                                <div className="col-span-2 text-right" title="Introduceți prețul FINAL (cu TVA inclus) — TVA-ul se calculează automat">Preț final (cu TVA)</div>
                                <div className="col-span-1 text-center">TVA%</div>
                                <div className="col-span-1 text-right">Total</div>
                                <div className="col-span-1"></div>
                            </div>
                            <div className="space-y-2 mt-2">
                                {(editing.items || []).map((it, i) => (
                                    <div key={i} className="grid grid-cols-12 gap-1 items-start" data-testid={`inv-item-${i}`}>
                                        <div className="col-span-5 relative">
                                            <input className="w-full input-soft !py-1.5 !text-sm" placeholder="Denumire articol *"
                                                   value={it.name}
                                                   onChange={(e) => {
                                                       const name = e.target.value;
                                                       setEditing(v => ({ ...v, items: v.items.map((x, j) => j === i ? { ...x, name } : x) }));
                                                       searchProducts(name, i);
                                                   }}
                                                   onBlur={() => setTimeout(() => setProductSuggs({ idx: -1, results: [] }), 200)}
                                            />
                                            {productSuggs.idx === i && productSuggs.results.length > 0 && (
                                                <div className="absolute z-30 top-full left-0 right-0 bg-card border border-border rounded-xl shadow-xl max-h-52 overflow-y-auto">
                                                    {productSuggs.results.map((p, pi) => (
                                                        <button key={pi} type="button"
                                                            onMouseDown={() => {
                                                                const price = Number(p.sale_price || p.price || 0);
                                                                setEditing(v => ({ ...v, items: v.items.map((x, j) => j === i ? { ...x, name: p.name, unit_price: price } : x) }));
                                                                setProductSuggs({ idx: -1, results: [] });
                                                            }}
                                                            className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex justify-between items-center border-b border-border last:border-0">
                                                            <span className="truncate">{p.name}</span>
                                                            <span className="text-muted-foreground text-xs ml-2 shrink-0">{(p.sale_price || p.price || 0).toFixed(2)} RON</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <input className="col-span-1 input-soft !py-1.5 !text-sm" placeholder="UM" value={it.unit || "buc"}
                                               onChange={(e) => setEditing(v => ({ ...v, items: v.items.map((x, j) => j === i ? { ...x, unit: e.target.value } : x) }))} />
                                        <input type="number" className="col-span-1 input-soft !py-1.5 !text-sm" placeholder="Cant"
                                               value={it.qty}
                                               onChange={(e) => setEditing(v => ({ ...v, items: v.items.map((x, j) => j === i ? { ...x, qty: Number(e.target.value) } : x) }))} />
                                        <input type="number" step="0.01" className="col-span-2 input-soft !py-1.5 !text-sm" placeholder="Preț cu TVA"
                                               value={it.unit_price}
                                               onChange={(e) => setEditing(v => ({ ...v, items: v.items.map((x, j) => j === i ? { ...x, unit_price: Number(e.target.value) } : x) }))} />
                                        <input type="number" className="col-span-1 input-soft !py-1.5 !text-sm" placeholder="TVA%" value={it.vat_rate ?? 19}
                                               onChange={(e) => setEditing(v => ({ ...v, items: v.items.map((x, j) => j === i ? { ...x, vat_rate: Number(e.target.value) } : x) }))} />
                                        <div className="col-span-1 pt-1.5">
                                            <div className="text-sm text-foreground">{((it.qty || 0) * (it.unit_price || 0)).toFixed(2)}</div>
                                            {(it.vat_rate || 0) > 0 && (
                                                <div className="text-[10px] text-muted-foreground">
                                                    bază: {((it.qty || 0) * (it.unit_price || 0) / (1 + (it.vat_rate || 0) / 100)).toFixed(2)}
                                                </div>
                                            )}
                                        </div>
                                        <button onClick={() => setEditing(v => ({ ...v, items: v.items.filter((_, j) => j !== i) }))}
                                                className="col-span-1 btn-ghost text-destructive"><Trash2 className="h-4 w-4" /></button>
                                    </div>
                                ))}
                            </div>
                            <button onClick={() => setEditing(v => ({ ...v, items: [...(v.items || []), emptyItem(v.vat_rate ?? defaultVat)] }))}
                                    className="text-sm text-primary mt-2 flex items-center gap-1" data-testid="inv-add-item">
                                <Plus className="h-3 w-3" /> Adaugă articol
                            </button>
                        </fieldset>

                        <div className="grid grid-cols-3 gap-2">
                            <select value={editing.payment_method || ""} onChange={(e) => setEditing(v => ({ ...v, payment_method: e.target.value }))}
                                    className="input-soft" data-testid="inv-payment-method">
                                <option value="">Nu afișa metodă de plată</option>
                                <option value="Transfer bancar">Transfer bancar</option>
                                <option value="Numerar">Numerar</option>
                                <option value="Card">Card</option>
                            </select>
                            <input type="date" value={editing.due_date || ""} onChange={(e) => setEditing(v => ({ ...v, due_date: e.target.value }))}
                                   placeholder={editing.kind === "offer" ? "Valabilă până la" : "Scadență"} className="input-soft"
                                   title={editing.kind === "offer" ? "Valabilă până la" : "Scadență"} />
                            <select value={editing.status || "draft"} onChange={(e) => setEditing(v => ({ ...v, status: e.target.value }))} className="input-soft" data-testid="inv-status">
                                {(STATUS_OPTIONS[editing.kind] || STATUS_OPTIONS.invoice).map(s => (
                                    <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                                ))}
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <label className="flex flex-col text-xs text-muted-foreground">
                                Cota TVA implicită (%)
                                <input type="number" step="0.01" value={editing.vat_rate ?? defaultVat}
                                       onChange={(e) => {
                                           const v = Number(e.target.value);
                                           setEditing(prev => ({
                                               ...prev,
                                               vat_rate: v,
                                               items: (prev.items || []).map(it => ({ ...it, vat_rate: v })),
                                           }));
                                       }}
                                       className="input-soft" data-testid="inv-vat-rate" />
                            </label>
                            <label className="flex flex-col text-xs text-muted-foreground">
                                Monedă
                                <input value={editing.currency || "RON"} onChange={(e) => setEditing(v => ({ ...v, currency: e.target.value.toUpperCase() }))} className="input-soft" />
                            </label>
                        </div>
                        <textarea rows="2" placeholder="Observații" value={editing.notes} onChange={(e) => setEditing(v => ({ ...v, notes: e.target.value }))} className="input-soft" />

                        <div className="bg-muted/40 p-3 rounded-lg flex justify-between items-center">
                            <div className="text-xs text-muted-foreground">
                                Bază (fără TVA): {totalsLine().subtotal.toFixed(2)}
                                {totalsLine().vat > 0 ? ` · TVA: ${totalsLine().vat.toFixed(2)}` : " · Neplătitor de TVA"}
                            </div>
                            <div className="font-heading text-2xl text-primary">
                                {totalsLine().total.toFixed(2)} {editing.currency || "RON"}
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 pt-2 border-t border-border">
                            <button onClick={() => setEditing(null)} className="btn-outline">Anulează</button>
                            <button onClick={save} className="btn-primary" data-testid="inv-save">Salvează</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
